import { useMemo } from "react";
import { FoodsIcon, FoodsTextInput, FoodsTooltip } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import { PrimaryGrid } from "src/components/Grid/layout/PrimaryGrid";
import { GridText } from "src/components/Grid/GridText";
import { MRT_Cell, MRT_Column, MRT_Row } from "material-react-table";
import { byteValueNumberFormatter, getMimeTypeLabel } from "src/utils/files";
import { useGridColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { defaultStyles, FileIcon } from "react-file-icon";
import i18next from "i18next";
import { mediaThumbnailStyling, mediaBorderStyling, fileIconStyling } from "../styles";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { MediaItem, useMediaLibraryContext } from "../context";
import { useFoodpilotContext, DELETE, MEDIA_CONTENT, VIEW } from "src/context/FoodpilotContext.tsx";

type MediaTableProps = {
    filterFunction?: (item: MediaItem) => boolean;
    rowClickHandler?: (item: MediaItem) => void;
};

export const MediaTable = (props: MediaTableProps) => {
    const { filterFunction, rowClickHandler } = props;
    const { t } = useTranslation();
    const { canUser } = useFoodpilotContext();
    const canView = canUser(VIEW, MEDIA_CONTENT);
    const canDelete = canUser(DELETE, MEDIA_CONTENT);
    const theme = useTheme();
    const mediaTableKey = "mediaLibraryTable";
    const {
        media,
        totalItems,
        sorting,
        setSorting,
        pagination,
        setPagination,
        columnFilters,
        setColumnFilters,
        mediaActions,
        onSelectItem,
    } = useMediaLibraryContext();

    const filteredMedia = useMemo(() => {
        return media?.filter((item: MediaItem) => (filterFunction ? filterFunction(item) : true)) || [];
    }, [media, filterFunction]);

    const getFileExtension = (filename: string): string => {
        const lastDot = filename.lastIndexOf(".");
        return lastDot !== -1 ? filename.substring(lastDot + 1).toLowerCase() : "";
    };

    const mimeTypeFilterOptions = useMemo(() => {
        const allMimeTypes = media?.map((item: MediaItem) => item.mimeType);
        const uniqueMimeTypes = [...new Set(allMimeTypes)];
        return uniqueMimeTypes
            .filter((mimeType): mimeType is string => !!mimeType)
            .map((mimeType) => ({
                value: mimeType,
                label: getMimeTypeLabel(mimeType),
            }));
    }, [media]);

    const mediaColumns = useMemo(
        () => [
            {
                accessorKey: "name",
                header: t("settings.media_library.table.name", "Name"),
                enableSorting: true,
                enableColumnFilter: true,
                Filter: ({ column }: { column: MRT_Column<MediaItem> }) => (
                    <FoodsTextInput
                        value={(column.getFilterValue() as string) ?? ""}
                        onChange={(value) => column.setFilterValue(value)}
                    />
                ),
                Cell: ({ row }: { row: MRT_Row<MediaItem> }) => {
                    const item = row.original;
                    const handleOpen =
                        canView && item.canView !== false ?
                            () => (onSelectItem ? onSelectItem(item) : mediaActions.handlePreview(item))
                        :   undefined;
                    const fileExtension = item.name ? getFileExtension(item.name) : "";
                    const isImage = item.mimeType?.startsWith("image/");
                    const isVideo = item.mimeType?.startsWith("video/");
                    const isPdf = item.mimeType === "application/pdf";
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                width: "100%",
                                gap: theme.spacing(2),
                            }}
                        >
                            <Box
                                sx={{
                                    width: theme.spacing(4),
                                    height: theme.spacing(4),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {isImage && (
                                    <Box
                                        component="img"
                                        src={item.url}
                                        alt={item.name}
                                        sx={{ ...mediaThumbnailStyling(theme), ...mediaBorderStyling(theme) }}
                                        onClick={handleOpen}
                                    />
                                )}
                                {isVideo && (
                                    <>
                                        <Box
                                            component="video"
                                            src={item.url}
                                            muted
                                            controls={false}
                                            preload="metadata"
                                            sx={{
                                                ...mediaThumbnailStyling(theme),
                                                ...mediaBorderStyling(theme),
                                                objectFit: "cover",
                                            }}
                                            onClick={handleOpen}
                                        />
                                    </>
                                )}
                                {isPdf && (
                                    <Box sx={{ ...fileIconStyling(theme) }} onClick={handleOpen}>
                                        <FileIcon
                                            extension={fileExtension}
                                            {...defaultStyles[fileExtension as keyof typeof defaultStyles]}
                                        />
                                    </Box>
                                )}
                                {!isImage && !isVideo && !isPdf && (
                                    <Box sx={{ ...fileIconStyling(theme) }} onClick={handleOpen}>
                                        <FileIcon
                                            extension={fileExtension}
                                            {...defaultStyles[fileExtension as keyof typeof defaultStyles]}
                                        />
                                    </Box>
                                )}
                            </Box>
                            <Box onClick={handleOpen}>
                                <GridText value={item.name} variant={"themed"} typographyVariant="body-bold" />
                            </Box>
                        </Box>
                    );
                },
                size: 280,
            },
            {
                Filter: ({ column }: { column: MRT_Column<MediaItem> }) => (
                    <FoodsTextInput
                        value={(column.getFilterValue() as string) ?? ""}
                        onChange={(value) => column.setFilterValue(value)}
                    />
                ),
                accessorKey: "mimeType",
                header: t("settings.media_library.table.type", "Type"),
                filterVariant: "select" as const,
                Cell: ({ cell }: { cell: MRT_Cell<MediaItem> }) => (
                    <FoodsTooltip title={cell.getValue<string>()} width="max-content" placement="top-start">
                        <Box>
                            <GridText value={getMimeTypeLabel(cell.getValue<string>())} />
                        </Box>
                    </FoodsTooltip>
                ),
                size: 150,
                enableColumnFilter: true,
            },
            {
                accessorKey: "size",
                header: t("settings.media_library.table.size", "Size"),
                enableColumnFilter: false,
                enableSorting: true,
                Cell: ({ cell }: { cell: MRT_Cell<MediaItem> }) => (
                    <GridText value={byteValueNumberFormatter(cell.getValue<number>())} />
                ),
                size: 120,
            },
            {
                accessorKey: "createdAt",
                header: t("settings.media_library.table.created_at", "Created"),
                enableSorting: true,
                enableColumnFilter: false,
                Cell: ({ cell }: { cell: MRT_Cell<MediaItem> }) => (
                    <GridText value={new Date(cell.getValue<string>()).toLocaleString(i18next.language)} />
                ),
                size: 180,
            },
        ],
        [t, theme, mimeTypeFilterOptions, canView],
    );

    const mrt_columns = useGridColumns(mediaColumns, mediaTableKey);

    const menuItems = useMemo(
        () => [
            {
                key: "preview",
                label: t("settings.media_library.table.action.preview", "Preview"),
                icon: <FoodsIcon icon="viewPwd" size={2} />,
                onClick: mediaActions.handlePreview,
                show: (item: MediaItem) =>
                    canView &&
                    item.canView !== false &&
                    (item.mimeType?.startsWith("image/") ||
                        item.mimeType?.startsWith("video/") ||
                        item.mimeType === "application/pdf"),
            },
            {
                key: "copy-url",
                label: t("common.actions.copy_url", "Copy URL"),
                icon: <ContentCopy fontSize="small" />,
                onClick: (item: MediaItem) => item.url && mediaActions.handleCopyUrl(item.url),
                show: (item: MediaItem) => canView && item.canView !== false && !!item.url,
            },
            {
                key: "download",
                label: t("common.actions.download", "Download"),
                icon: <FoodsIcon icon="importUpload" size={2} />,
                onClick: mediaActions.handleDownload,
                show: (item: MediaItem) => canView && item.canView !== false,
            },
            ...(canDelete ?
                [
                    {
                        key: "delete",
                        label: t("common.actions.delete", "Delete"),
                        icon: <FoodsIcon icon="delete" size={2} />,
                        onClick: (item: MediaItem) => mediaActions.handleDelete(item.id),
                        show: (item: MediaItem) => item.canDelete !== false,
                        divider: true,
                    },
                ]
            :   []),
        ],
        [t, mediaActions, canView, canDelete],
    );

    return (
        <PrimaryGrid
            enableViews
            gridKey={mediaTableKey}
            columns={mrt_columns.mrt_columns}
            data={filteredMedia}
            filters={{
                columnFilters: columnFilters ?? [],
                setColumnFilters: setColumnFilters,
            }}
            menuItems={menuItems}
            emptyMessage={t("settings.media_library.no_media", "No media found")}
            sort={{
                sorting: sorting ?? [],
                setSorting: setSorting,
            }}
            pagination={{
                pagination: pagination ?? { pageIndex: 0, pageSize: 20 },
                setPagination: setPagination,
                totalItems: totalItems || 0,
            }}
            columnConfig={mrt_columns}
            onRowClick={(row) => {
                if (onSelectItem) {
                    onSelectItem(row);
                } else if (rowClickHandler) {
                    rowClickHandler(row);
                } else {
                    mediaActions.handleToggleSelect(row.id);
                }
            }}
        />
    );
};
